import { useCallback, useState, type CSSProperties, type PointerEvent } from 'react'
import { homepageDemoSlab } from './homepageDemoSlab'
import { Slab } from './share/components/slab/Slab'
import styles from './HomepageTiltSlab.module.css'

const neutralPointer = { x: 0.5, y: 0.5 }
const neutralTilt = { rotateX: 0, rotateY: 0 }

export function HomepageTiltSlab({ className = '' }: { className?: string }) {
  const [tilt, setTilt] = useState(neutralTilt)
  const [pointer, setPointer] = useState(neutralPointer)
  const [isHovering, setIsHovering] = useState(false)
  const [isPressed, setIsPressed] = useState(false)

  const handlePointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    const x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    const y = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))

    setIsHovering(true)
    setPointer({ x, y })
    setTilt({
      rotateX: (0.5 - y) * 12,
      rotateY: (x - 0.5) * 12,
    })
  }, [])

  const resetPointer = () => {
    setIsHovering(false)
    setIsPressed(false)
    setPointer(neutralPointer)
    setTilt(neutralTilt)
  }

  const slabStyle = {
    '--pointer-x': `${pointer.x * 100}%`,
    '--pointer-y': `${pointer.y * 100}%`,
    '--holo-a': `${120 + pointer.x * 60 + pointer.y * 30}deg`,
    '--holo-b': `${135 + pointer.x * 90 + pointer.y * 45}deg`,
    '--holo-c': `${90 + pointer.x * 180}deg`,
    '--sweep-start': `${35 + pointer.y * 20}%`,
    '--sweep-mid': `${48 + pointer.y * 10}%`,
    '--sweep-end': `${60 + pointer.y * 20}%`,
    transform: `perspective(1000px) rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) scale(${isPressed ? 0.99 : 1})`,
    transition: isHovering ? 'transform 0.1s ease-out' : 'transform 0.4s ease-out',
  } as CSSProperties

  return (
    <div className={`${styles.wrap} ${className}`}>
      <svg className={styles.svgDefs} aria-hidden="true">
        <defs>
          <filter id="homepage-holo-noise" x="0%" y="0%" width="100%" height="100%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.9"
              numOctaves="4"
              seed="2"
              result="noise"
            />
            <feColorMatrix type="saturate" values="2.5" in="noise" result="colorNoise" />
            <feBlend in="SourceGraphic" in2="colorNoise" mode="screen" />
          </filter>
        </defs>
      </svg>

      <div
        className={`${styles.scene} ${isHovering ? styles.hovering : ''}`}
        style={slabStyle}
        onPointerEnter={() => setIsHovering(true)}
        onPointerMove={handlePointerMove}
        onPointerLeave={resetPointer}
        onPointerDown={() => setIsPressed(true)}
        onPointerUp={() => setIsPressed(false)}
      >
        <Slab
          className={styles.innerSlab}
          showActions={false}
          flipEnabled={false}
          samplePalette={false}
          {...homepageDemoSlab}
        />
        <div className={styles.pointerGlow} />
        <div className={styles.holoColor} />
        <div className={styles.holoNoise} />
        <div className={styles.holoSweep} />
      </div>
    </div>
  )
}
